import type { PlayerId } from './ids';

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 16;
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

/** Letters (including æ, ø, å), digits, hyphen and underscore. No spaces, so it is easy to type at login. */
export const USERNAME_PATTERN = /^[\p{L}\p{N}_-]+$/u;

/** Same easy-to-read alphabet as room codes. Shown as `ABCD-EFGH`. */
export const INVITE_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const INVITE_CODE_LENGTH = 8;

export type UserRole = 'player' | 'admin';

/** The logged-in user as the web app sees it. The id is also the player id in every game. */
export interface AuthUser {
  id: PlayerId;
  username: string;
  displayName: string;
  role: UserRole;
}

export interface RegisterRequest {
  /** Only needed for the bootstrap admin invitation. Players sign up without one. */
  inviteCode?: string;
  username: string;
  password: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface BlockUserRequest {
  username: string;
  blocked: boolean;
}

export type AuthErrorCode =
  | 'INVALID_REQUEST'
  | 'RATE_LIMITED'
  | 'INVALID_CREDENTIALS'
  | 'ACCOUNT_BLOCKED'
  | 'INVITE_INVALID'
  | 'USERNAME_TAKEN'
  | 'USERNAME_NOT_ALLOWED'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  /** The profile asked to wear something the player has not bought. */
  | 'NOT_OWNED'
  /** The avatar shop: not enough PixelCoins, or the item is owned already. */
  | 'NOT_ENOUGH_COINS'
  | 'ALREADY_OWNED';

export interface ApiError {
  error: AuthErrorCode | 'NOT_FOUND' | 'INTERNAL_ERROR';
}

/** Removes spaces and dashes and uppercases, so `abcd-efgh` becomes `ABCDEFGH`. */
export function normalizeInviteCode(input: string): string {
  return input.replace(/[\s-]+/g, '').toUpperCase();
}

const INVITE_CODE_PATTERN = new RegExp(`^[${INVITE_CODE_ALPHABET}]{${INVITE_CODE_LENGTH}}$`);

export function isInviteCode(value: string): boolean {
  return INVITE_CODE_PATTERN.test(value);
}

export function formatInviteCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}

/** Usernames are unique regardless of case: `Ida` and `ida` are the same account. */
export function usernameKey(username: string): string {
  return username.trim().toLowerCase();
}
